import { lazy, ReactElement } from "react";
import { RouteObject } from "react-router-dom";
import { EffectiveAccessGuard } from "@feature/auth";

const Communication = lazy(() =>
  import("@feature/communication").then((m) => ({ default: m.Communication })),
);
const Phenomenon = lazy(() =>
  import("@feature/communication").then((m) => ({ default: m.Phenomenon })),
);
const ListAlerts = lazy(() =>
  import("@feature/alerts").then((m) => ({ default: m.ListAlerts })),
);
const CreateForecast = lazy(() =>
  import("@feature/createForecast").then((m) => ({
    default: m.CreateForecast,
  })),
);
const CreateMultipleForecast = lazy(() =>
  import("@feature/createMultipleForecast").then((m) => ({
    default: m.CreateMultipleForecast,
  })),
);
const CreatePhenomenon = lazy(() =>
  import("@feature/createPhenomenon").then((m) => ({
    default: m.CreatePhenomenon,
  })),
);
const CreateDrill = lazy(() =>
  import("@feature/createDrill").then((m) => ({ default: m.CreateDrill })),
);
const AlertDetails = lazy(() =>
  import("@feature/alertDetails").then((m) => ({ default: m.AlertDetails })),
);

import { ROUTES } from "@shared/constants/routes.constants";

const withReadAlert = (element: ReactElement) => (
  <EffectiveAccessGuard codes={["read:alert"]} redirect>
    {element}
  </EffectiveAccessGuard>
);

const isWildlifeEnabled = import.meta.env.VITE_FEATURE_FLAG_WILDLIFE === "true";
const isFogEnabled = import.meta.env.VITE_FEATURE_FLAG_FOG === "true";
const isMarinePhenomenaEnabled =
  import.meta.env.VITE_FEATURE_FLAG_MARINEPHENOMENA === "true";

const listAlertsPhenomenonTypes = [
  "storm",
  "flood",
  "temp_flood",
  "wind",
  "drill",
  "frost",
  "tropical_cyclone",
  "forest_fire",
  "volcanic",
  "low_temperatures",
  "earthquake",
  "tsunami",
  "high_temperatures",
  "environmental_risk",
  "hail",
  "dust_devil",
  ...(isFogEnabled ? ["fog"] : []),
  ...(isMarinePhenomenaEnabled ? ["waves", "swell", "river_rise"] : []),
].join(",");

export const communicationRoutes: RouteObject[] = [
  {
    path: ROUTES.COMMUNICATION.ROOT,
    element: withReadAlert(<Communication />),
  },
  {
    path: ROUTES.COMMUNICATION.LIST_ALERTS_PHENOMENON,
    element: withReadAlert(
      <ListAlerts type={listAlertsPhenomenonTypes} />,
    ),
  },
  {
    path: ROUTES.COMMUNICATION.LIST_ALERTS_PHENOMENON_BY_TYPE,
    element: withReadAlert(<ListAlerts />),
  },
  {
    path: ROUTES.COMMUNICATION.LIST_ALERTS_INFO,
    element: withReadAlert(
      <ListAlerts
        type={
          isWildlifeEnabled
            ? "info,traffic,security,wildlife"
            : "info,traffic,security"
        }
      />,
    ),
  },
  {
    path: ROUTES.COMMUNICATION.LIST_ALERTS_INFO_BY_TYPE,
    element: withReadAlert(<ListAlerts />),
  },
  {
    path: ROUTES.COMMUNICATION.LIST_ALERTS_PRONOSTICS,
    element: withReadAlert(<ListAlerts type="forecast" />),
  },
  {
    path: ROUTES.COMMUNICATION.LIST_ALERTS_PRONOSTICS_BY_TYPE,
    element: withReadAlert(<ListAlerts />),
  },
  {
    path: ROUTES.COMMUNICATION.LIST_ALERTS_NEWS,
    element: withReadAlert(<ListAlerts type="news" />),
  },
  {
    path: ROUTES.COMMUNICATION.LIST_ALERTS_NEWS_BY_TYPE,
    element: withReadAlert(<ListAlerts />),
  },
  {
    path: ROUTES.COMMUNICATION.CREATE_FORECAST,
    element: withReadAlert(<CreateForecast />),
  },
  {
    path: ROUTES.COMMUNICATION.CREATE_MULTIPLE_FORECAST,
    element: withReadAlert(<CreateMultipleForecast />),
  },
  {
    path: ROUTES.COMMUNICATION.INFO,
    element: withReadAlert(<Phenomenon type="info" />),
  },
  {
    path: ROUTES.COMMUNICATION.INFO_TYPES.GENERAL,
    element: withReadAlert(<CreatePhenomenon type="info" />),
  },
  {
    path: ROUTES.COMMUNICATION.INFO_TYPES.TRAFFIC,
    element: withReadAlert(<CreatePhenomenon type="traffic" />),
  },
  {
    path: ROUTES.COMMUNICATION.INFO_TYPES.SECURITY,
    element: withReadAlert(<CreatePhenomenon type="security" />),
  },
  ...(isWildlifeEnabled
    ? [
        {
          path: ROUTES.COMMUNICATION.INFO_TYPES.WILDLIFE,
          element: withReadAlert(<CreatePhenomenon type="wildlife" />),
        },
      ]
    : []),
  {
    path: ROUTES.COMMUNICATION.NEWS,
    element: withReadAlert(<CreatePhenomenon type="news" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON,
    element: withReadAlert(<Phenomenon />),
  },
  ...(isFogEnabled
    ? [
        {
          path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.FOG,
          element: withReadAlert(<CreatePhenomenon type="fog" />),
        },
      ]
    : []),
  ...(isMarinePhenomenaEnabled
    ? [
        {
          path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.WAVES,
          element: withReadAlert(<CreatePhenomenon type="waves" />),
        },
        {
          path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.SWELL,
          element: withReadAlert(<CreatePhenomenon type="swell" />),
        },
        {
          path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.RIVER_RISE,
          element: withReadAlert(<CreatePhenomenon type="river_rise" />),
        },
      ]
    : []),
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.FLOOD,
    element: withReadAlert(<CreatePhenomenon type="temp_flood" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.STORM,
    element: withReadAlert(<CreatePhenomenon type="storm" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.HAIL,
    element: withReadAlert(<CreatePhenomenon type="hail" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.WIND,
    element: withReadAlert(<CreatePhenomenon type="wind" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.DRILL,
    element: withReadAlert(<CreateDrill />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.FROST,
    element: withReadAlert(<CreatePhenomenon type="frost" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.TROPICAL_CYCLONE,
    element: withReadAlert(<CreatePhenomenon type="tropical_cyclone" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.FOREST_FIRE,
    element: withReadAlert(<CreatePhenomenon type="forest_fire" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.DUST_DEVIL,
    element: withReadAlert(<CreatePhenomenon type="dust_devil" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.VOLCANIC,
    element: withReadAlert(<CreatePhenomenon type="volcanic" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.LOW_TEMPERATURES,
    element: withReadAlert(<CreatePhenomenon type="low_temperatures" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.HIGH_TEMPERATURES,
    element: withReadAlert(<CreatePhenomenon type="high_temperatures" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.EARTHQUAKE,
    element: withReadAlert(<CreatePhenomenon type="earthquake" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.TSUNAMI,
    element: withReadAlert(<CreatePhenomenon type="tsunami" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.ENVIRONMENTAL_RISK,
    element: withReadAlert(<CreatePhenomenon type="environmental_risk" />),
  },
  {
    path: ROUTES.COMMUNICATION.PHENOMENON_TYPES.SILENT,
    element: withReadAlert(<CreatePhenomenon type="silent" />),
  },
  {
    path: ROUTES.COMMUNICATION.ALERT_DETAILS.PHENOMENON,
    element: withReadAlert(<AlertDetails />),
  },
  {
    path: ROUTES.COMMUNICATION.ALERT_DETAILS.INFO,
    element: withReadAlert(<AlertDetails />),
  },
  {
    path: ROUTES.COMMUNICATION.ALERT_DETAILS.PRONOSTICS,
    element: withReadAlert(<AlertDetails />),
  },
  {
    path: ROUTES.COMMUNICATION.ALERT_DETAILS.NEWS,
    element: withReadAlert(<AlertDetails />),
  },
];
